import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, Lock, ShieldCheck } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const LOGO_URL = "/images/logo-full.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      return;
    }

    const validate = async () => {
      try {
        const [tokenRes, policyRes] = await Promise.all([
          axios.get(`${API_URL}/api/auth/password-reset/validate/${encodeURIComponent(token)}`),
          axios.get(`${API_URL}/api/auth/password-policy`),
        ]);
        setPolicy(policyRes.data);
        if (tokenRes.data?.valid) {
          setTokenValid(true);
          const email = tokenRes.data.email || "";
          if (email.includes("@")) {
            const [local, domain] = email.split("@");
            const masked = local.length <= 2
              ? `${local[0]}***@${domain}`
              : `${local.slice(0, 2)}***@${domain}`;
            setMaskedEmail(masked);
          }
        }
      } catch {
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };

    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/password-reset/confirm`, {
        token,
        new_password: password,
      });
      toast.success("Password aggiornata. Puoi effettuare il login.");
      navigate("/login");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore durante l'aggiornamento della password");
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-[#f8fbfa] flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-emerald-200 border-t-emerald-600 animate-spin" />
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-[#f8fbfa] flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <img src={LOGO_URL} alt="Affidya" className="mx-auto mb-4 h-12" />
          <h1 className="text-xl font-bold text-slate-900">Link non valido o scaduto</h1>
          <p className="mt-2 text-sm text-slate-500">
            Richiedi un nuovo codice di verifica per reimpostare la password.
          </p>
          <Link to="/forgot-password" className="mt-6 inline-block">
            <Button className="rounded-xl bg-emerald-600 hover:bg-emerald-700">
              Recupera password
            </Button>
          </Link>
          <div className="mt-4">
            <Link to="/login" className="text-sm text-emerald-700 hover:underline">
              Torna al login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fbfa] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <img src={LOGO_URL} alt="Affidya" className="mx-auto mb-4 h-12" />
          <h1 className="text-2xl font-bold text-slate-900">Nuova password</h1>
          {maskedEmail && (
            <p className="mt-1 text-sm text-slate-500">Account: {maskedEmail}</p>
          )}
        </div>

        <section className="rounded-[1.6rem] border border-white/80 bg-white p-6 shadow-[0_24px_70px_rgba(15,23,42,0.10)]">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-password" className="text-sm font-medium text-slate-700">
                Nuova password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <Input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 rounded-xl pl-11 pr-11"
                  required
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm-password" className="text-sm font-medium text-slate-700">
                Conferma password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <Input
                  id="confirm-password"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-11 rounded-xl pl-11"
                  required
                />
              </div>
            </div>

            {policy?.description && (
              <div className="flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
                <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5 text-emerald-600" />
                <p>{policy.description}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="h-11 w-full rounded-xl bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? "Salvataggio..." : "Imposta nuova password"}
            </Button>
          </form>
        </section>

        <div className="mt-4 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Torna al login
          </Link>
        </div>
      </div>
    </div>
  );
}
